import { Dropdown, Menu, MenuProps } from 'antd';
import styled, { CSSProperties } from 'styled-components';

import { NorlysFont } from '~src/branding';
import { Anchor } from '~src/components/Anchor';
import { Button, ButtonNorlys } from '~src/components/Button';
import { Icon } from '~src/components/Icon';
import { Heading } from '~src/components/Typography';
import { BODIL_ENERGI, NORLYS_BOOK_URL, getBookingURLByLeadOrigin } from '~src/constants';
import { useDisplayContext } from '~src/context/DisplayContext';
import { LEAD_ORIGIN_DISTRICT_HEATING_FLYER, useLeadOrigin } from '~src/hooks/useLeadOrigin';
import { useNavigation } from '~src/hooks/useNavigation';

const HeaderButton = styled.button<{ align: 'left' | 'right' }>(({ align }) => ({
  ...(align === 'left' ? { left: '20px' } : { right: '20px' }),
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: '50%',
  transform: 'translate(0, -50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
}));

const ITEMS_BASE = [{ key: 'hvorfor-bodil', label: 'Hvorfor Bodil?' }];

type MenuItems = NonNullable<MenuProps['items']>;

type LinksProps = {
  buttonStyle?: CSSProperties;
  itemStyle?: CSSProperties;
};

export const Links = ({ buttonStyle: buttonStyleInput, itemStyle: itemStyleInput }: LinksProps) => {
  const { isMobile } = useDisplayContext();
  const { origin } = useLeadOrigin();

  const buttonStyle = { color: 'white', ...buttonStyleInput };

  if (isMobile) {
    return (
      <MobileKebabMenu
        {...{
          itemStyle: { ...itemStyleInput, color: 'black' },
          buttonStyle,
        }}
      />
    );
  }

  return (
    <>
      <DesktopMenuItems itemStyle={{ ...itemStyleInput, color: 'white' }} />

      {/* Every partner has its own booking page. This knew only EDC and sent
          everyone else to the generic Bodil link, so /jyskebank and /nordea got
          neither. Shared with the estimate-page CTA now. */}
      <Anchor newTab href={getBookingURLByLeadOrigin(origin)}>
        {origin !== LEAD_ORIGIN_DISTRICT_HEATING_FLYER && (
          <Button style={{ margin: 0, ...buttonStyle }} onClick={() => false} type='callToAction'>
            {/* Partners land on a real booking page too, so the button can say
                what it does instead of falling back to "Ring mig op". */}
            Book tid
          </Button>
        )}
      </Anchor>
    </>
  );
};

export const LinksNorlys = ({ buttonStyle: buttonStyleInput, itemStyle: itemStyleInput }: LinksProps) => {
  const { isMobile } = useDisplayContext();

  const itemStyle = { ...NorlysFont.style('default', 16), color: 'black', ...itemStyleInput };
  const buttonStyle = { color: 'black', ...buttonStyleInput };

  if (isMobile) {
    return <MobileKebabMenu {...{ itemStyle, buttonStyle }} />;
  }

  return (
    <>
      <DesktopMenuItems {...{ itemStyle, items: [{ key: 'hvorfor-bodil', label: 'Hvorfor Bodil?' }] }} />

      <Anchor newTab href={NORLYS_BOOK_URL}>
        <ButtonNorlys style={{ margin: 0, ...buttonStyle, color: 'white' }} onClick={() => false}>
          Book tid
        </ButtonNorlys>
      </Anchor>
    </>
  );
};

type HeaderButtonsProps = {
  back?: string;
  forward?: string;
  textColor?: string;
};

export const HeaderButtons = ({ back, forward, textColor = 'white' }: HeaderButtonsProps) => {
  const iconProps = { height: 20, width: 20, color: textColor };
  const buttonStyle = { color: textColor };

  const { navigate } = useNavigation();

  return (
    <>
      {back && (
        <HeaderButton style={buttonStyle} align='left' onClick={() => navigate(back)}>
          <Icon.Arrow direction='left' {...iconProps} />
        </HeaderButton>
      )}
      {forward && (
        <HeaderButton style={buttonStyle} align='right' onClick={() => navigate(forward)}>
          <Icon.Arrow direction='right' {...iconProps} />
        </HeaderButton>
      )}
    </>
  );
};

type HeaderProps = {
  itemStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
  items?: { key: string; label: string }[];
  top?: string | number;
};

const MobileKebabMenu = ({ itemStyle, buttonStyle, items = ITEMS_BASE, top }: HeaderProps) => {
  const menuItems = [
    ...items.map(({ key, label }) => ({
      label: (
        <Anchor style={itemStyle} newTab href={`${BODIL_ENERGI}/${key}/`}>
          {label}
        </Anchor>
      ),
      key,
    })),
  ] as MenuItems;

  return (
    <div style={{ position: 'absolute', right: '16px', top: top ?? '32px' }}>
      <Dropdown overlay={<Menu style={{ zIndex: 20e3 }} items={menuItems} />} trigger={['click']}>
        <div>
          <Icon.ThreeDots width={30} height={30} color={buttonStyle?.color} style={buttonStyle} />
        </div>
      </Dropdown>
    </div>
  );
};

const DesktopMenuItems = ({ itemStyle, items = ITEMS_BASE }: HeaderProps) => {
  return (
    <>
      {items.map(({ key, label }) => (
        <Heading
          level={2}
          key={key}
          color='light'
          style={{
            width: 'fit-content',
            padding: '16px 24px',
            right: '60px',
            ...itemStyle,
          }}
        >
          <Anchor newTab href={`${BODIL_ENERGI}/${key}/`}>
            {label}
          </Anchor>
        </Heading>
      ))}
    </>
  );
};
